import { create } from 'zustand'

const useListsStore = create((set, get) => ({
  // State
  lists: [],
  selectedListIds: [], // Array of selected list IDs
  
  // UI States
  bulkDeleteDialogOpen: false,
  actionLoading: {}, // Object to track loading states by action key
  
  // Actions
  setLists: (lists) => set({ lists: Array.isArray(lists) ? lists : [] }),
  
  addList: (newList) => set((state) => ({
    lists: [newList, ...(state.lists || [])]
  })),
  
  updateList: (listId, updatedList) => set((state) => ({
    lists: (state.lists || []).map(list => 
      list.id === listId ? { ...updatedList } : list
    ),
  })),
  
  reorderLists: (reorderedLists) => set({ lists: reorderedLists }),
  
  // Task-specific actions for optimistic updates
  updateTaskInList: (listId, taskId, taskUpdates) => set((state) => ({
    lists: (state.lists || []).map(list =>
      list.id === listId
        ? {
            ...list,
            tasks: (list.tasks || []).map(task =>
              task.id === taskId
                ? { ...task, ...taskUpdates }
                : task
            )
          }
        : list
    ),
  })),
  
  completeAllTasksInList: (listId) => set((state) => ({
    lists: (state.lists || []).map(list =>
      list.id === listId
        ? {
            ...list,
            tasks: (list.tasks || []).map(task => ({ ...task, completed: true }))
          }
        : list
    ),
  })),
  
  incompleteAllTasksInList: (listId) => set((state) => ({
    lists: (state.lists || []).map(list =>
      list.id === listId
        ? {
            ...list,
            tasks: (list.tasks || []).map(task => ({ ...task, completed: false }))
          }
        : list
    ),
  })),
  
  // Selection management actions
  toggleListSelection: (listId) => set((state) => {
    const isSelected = state.selectedListIds.includes(listId);
    return {
      selectedListIds: isSelected
        ? state.selectedListIds.filter(id => id !== listId)
        : [...state.selectedListIds, listId]
    };
  }),
  
  clearSelection: () => set({ selectedListIds: [] }),
  
  selectAllLists: () => set((state) => ({
    selectedListIds: (state.lists || []).map(list => list.id)
  })),
  
  isListSelected: (listId) => {
    const state = get();
    return state.selectedListIds.includes(listId);
  },
  
  getSelectedCount: () => {
    const state = get();
    return state.selectedListIds.length;
  },
  
  // Remove selected list IDs when lists are deleted
  removeList: (listId) => set((state) => ({
    lists: (state.lists || []).filter(list => list.id !== listId),
    selectedListIds: state.selectedListIds.filter(id => id !== listId)
  })),

  // UI state management actions
  setBulkDeleteDialogOpen: (open) => set({ bulkDeleteDialogOpen: open }),
  
  setActionLoading: (actionKey, loading) => set((state) => ({
    actionLoading: {
      ...state.actionLoading,
      [actionKey]: loading
    }
  })),
  
  clearActionLoading: (actionKey) => set((state) => {
    const newActionLoading = { ...state.actionLoading };
    delete newActionLoading[actionKey];
    return { actionLoading: newActionLoading };
  }),

  // Reset store (useful for logout)
  reset: () => set({
    lists: [],
    selectedListIds: [],
    bulkDeleteDialogOpen: false,
    actionLoading: {}
  })
}))

export default useListsStore
